import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Form, InputGroup, Pagination, Table } from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaSearch, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { adminApi } from "../../api/services";
import { getErrorMessage } from "../../api/client";
import OrderDetailsPanel from "../../components/OrderDetailsPanel";
import { hasShippingDetails } from "../../utils/orderAddress";
import {
  buildPageItems,
  filterAndSortOrders,
  ORDER_EMIRATES,
  ORDER_PAGE_SIZES,
  ORDER_SORT_OPTIONS,
  paginateItems,
} from "../../utils/adminOrders";
import { TableSkeleton } from "../../components/Skeleton";
import "./ManagePages.css";

const ORDER_STATUSES = [
  "pending",
  "confirmed",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
];

const STATUS_VARIANT = {
  pending: "secondary",
  confirmed: "info",
  processing: "primary",
  shipped: "warning",
  delivered: "success",
  cancelled: "danger",
};

const DEFAULT_FILTERS = {
  search: "",
  status: "all",
  emirate: "all",
  dateFrom: "",
  dateTo: "",
  sortBy: "newest",
};

const ManageOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.orders();
      setOrders(res.data.data.orders || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load orders"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredOrders = useMemo(
    () => filterAndSortOrders(orders, filters),
    [orders, filters]
  );

  const pagination = useMemo(
    () => paginateItems(filteredOrders, page, pageSize),
    [filteredOrders, page, pageSize]
  );

  useEffect(() => {
    if (page > pagination.totalPages) {
      setPage(pagination.totalPages);
    }
  }, [page, pagination.totalPages]);

  const hasActiveFilters =
    filters.search.trim() ||
    filters.status !== "all" ||
    filters.emirate !== "all" ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.sortBy !== "newest";

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setExpandedId(null);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
    setExpandedId(null);
  };

  const changePageSize = (value) => {
    setPageSize(Number(value));
    setPage(1);
    setExpandedId(null);
  };

  const changeStatus = async (order, status) => {
    setSavingId(order._id);
    try {
      const res = await adminApi.updateOrderStatus(order._id, status);
      const updated = res.data.data.order;
      setOrders((prev) =>
        prev.map((o) => (o._id === updated._id ? { ...o, status: updated.status } : o))
      );
      toast.success("Order status updated");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not update status"));
    } finally {
      setSavingId(null);
    }
  };

  const toggleExpanded = (orderId) => {
    setExpandedId((current) => (current === orderId ? null : orderId));
  };

  const pageItems = buildPageItems(pagination.page, pagination.totalPages);

  return (
    <div className="manage-page">
      <header className="manage-head">
        <div>
          <h1>All orders</h1>
          <p>Search, filter, sort, and browse customer orders before fulfilment.</p>
        </div>
        <span className="manage-count">
          {filteredOrders.length === orders.length
            ? `${orders.length} total`
            : `${filteredOrders.length} of ${orders.length}`}
        </span>
      </header>

      {!loading && orders.length > 0 && (
        <div className="manage-toolbar">
          <InputGroup className="manage-search">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search order #, customer, email, phone, address…"
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
            />
          </InputGroup>

          <Form.Select
            className="manage-filter"
            value={filters.status}
            onChange={(e) => updateFilter("status", e.target.value)}
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            {ORDER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="manage-filter"
            value={filters.emirate}
            onChange={(e) => updateFilter("emirate", e.target.value)}
            aria-label="Filter by emirate"
          >
            <option value="all">All emirates</option>
            {ORDER_EMIRATES.map((emirate) => (
              <option key={emirate} value={emirate}>
                {emirate}
              </option>
            ))}
          </Form.Select>

          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateFrom}
            onChange={(e) => updateFilter("dateFrom", e.target.value)}
            aria-label="From date"
          />

          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateTo}
            onChange={(e) => updateFilter("dateTo", e.target.value)}
            aria-label="To date"
          />

          <Form.Select
            className="manage-filter"
            value={filters.sortBy}
            onChange={(e) => updateFilter("sortBy", e.target.value)}
            aria-label="Sort orders"
          >
            {ORDER_SORT_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="manage-filter manage-page-size"
            value={pageSize}
            onChange={(e) => changePageSize(e.target.value)}
            aria-label="Rows per page"
          >
            {ORDER_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Form.Select>

          {hasActiveFilters && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="manage-clear-btn"
              onClick={clearFilters}
            >
              <FaTimes /> Clear
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={8} cols={7} />
      ) : orders.length === 0 ? (
        <p className="manage-empty">No orders yet.</p>
      ) : filteredOrders.length === 0 ? (
        <div className="manage-empty">
          <p>No orders match your search or filters.</p>
          <Button variant="link" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <>
          <div className="manage-card table-responsive">
            <Table hover className="manage-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Delivery</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Placed</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pagination.items.map((order) => {
                  const shipping = order.shippingAddress || {};
                  const isExpanded = expandedId === order._id;

                  return (
                    <React.Fragment key={order._id}>
                      <tr>
                        <td>
                          <div className="manage-order-code">
                            <code>{order.orderNumber}</code>
                            <Button
                              variant="link"
                              size="sm"
                              className="manage-expand-btn"
                              onClick={() => toggleExpanded(order._id)}
                            >
                              {isExpanded ? (
                                <>
                                  <FaChevronUp /> Hide
                                </>
                              ) : (
                                <>
                                  <FaChevronDown /> Details
                                </>
                              )}
                            </Button>
                          </div>
                        </td>
                        <td>
                          <strong>{shipping.fullName || order.user?.name || "—"}</strong>
                          <div className="manage-muted">
                            {shipping.email || order.user?.email || "—"}
                          </div>
                          {(shipping.phone || order.user?.phone) && (
                            <div className="manage-muted">
                              {shipping.phone || order.user?.phone}
                            </div>
                          )}
                        </td>
                        <td>
                          {hasShippingDetails(shipping) ? (
                            <>
                              <div>{shipping.emirate || shipping.city || "—"}</div>
                              {shipping.companyName && (
                                <div className="manage-muted">{shipping.companyName}</div>
                              )}
                            </>
                          ) : (
                            <span className="manage-muted">—</span>
                          )}
                        </td>
                        <td>{order.items?.length || 0}</td>
                        <td>AED {Number(order.total || 0).toFixed(2)}</td>
                        <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                        <td className="text-nowrap">
                          <Badge
                            bg={STATUS_VARIANT[order.status] || "secondary"}
                            className="me-2"
                          >
                            {order.status}
                          </Badge>
                          <Form.Select
                            size="sm"
                            className="manage-select"
                            value={order.status}
                            disabled={savingId === order._id}
                            onChange={(e) => changeStatus(order, e.target.value)}
                          >
                            {ORDER_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="manage-order-details-row">
                          <td colSpan={7}>
                            <OrderDetailsPanel order={order} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="manage-pagination-bar">
            <p className="manage-pagination-meta">
              Showing {pagination.showingFrom}–{pagination.showingTo} of{" "}
              {pagination.total} orders
            </p>

            {pagination.totalPages > 1 && (
              <Pagination className="manage-pagination mb-0">
                <Pagination.Prev
                  disabled={pagination.page <= 1}
                  onClick={() => {
                    setPage((p) => Math.max(1, p - 1));
                    setExpandedId(null);
                  }}
                />
                {pageItems.map((item, index) =>
                  item === "…" ? (
                    <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={item}
                      active={item === pagination.page}
                      onClick={() => {
                        setPage(item);
                        setExpandedId(null);
                      }}
                    >
                      {item}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => {
                    setPage((p) => Math.min(pagination.totalPages, p + 1));
                    setExpandedId(null);
                  }}
                />
              </Pagination>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ManageOrders;
